import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button } from '../components/UI';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';

const InnovationRoadmap = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <SharedHeader />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('innovationRoadmapTitle')}
            </h1>
            <p className="text-xl text-gray-300">
              {t('innovationRoadmapSubtitle')}
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-500 bg-opacity-20 border-2 border-yellow-500 rounded-lg p-6 mb-12">
            <p className="text-yellow-200 text-center">
              {t('roadmapDisclaimer')}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {/* Section 1: Dónde estamos hoy */}
            <section className="bg-purple-800 bg-opacity-30 rounded-lg p-8 backdrop-blur">
              <h2 className="text-2xl font-bold mb-4 text-yellow-400">
                {t('roadmapCurrentTitle')}
              </h2>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapCurrentPoint1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapCurrentPoint2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapCurrentPoint3')}</span>
                </li>
              </ul>
            </section>

            {/* Section 2: Próximos 12-24 meses */}
            <section className="bg-purple-800 bg-opacity-30 rounded-lg p-8 backdrop-blur">
              <h2 className="text-2xl font-bold mb-4 text-yellow-400">
                {t('roadmapImplementationTitle')}
              </h2>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImplementationPoint1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImplementationPoint2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImplementationPoint3')}</span>
                </li>
              </ul>
            </section>

            {/* Section 3: Línea de tiempo IA */}
            <section className="bg-purple-800 bg-opacity-30 rounded-lg p-8 backdrop-blur">
              <h2 className="text-2xl font-bold mb-4 text-yellow-400">
                {t('roadmapAITitle')}
              </h2>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">🤖</span>
                  <span>{t('roadmapAIPoint1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">🤖</span>
                  <span>{t('roadmapAIPoint2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">🤖</span>
                  <span>{t('roadmapAIPoint3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">🤖</span>
                  <span>{t('roadmapAIPoint4')}</span>
                </li>
              </ul>
            </section>

            {/* Section 4: Impacto esperado en Chile */}
            <section className="bg-purple-800 bg-opacity-30 rounded-lg p-8 backdrop-blur">
              <h2 className="text-2xl font-bold mb-4 text-yellow-400">
                {t('roadmapImpactTitle')}
              </h2>
              <ul className="space-y-3 text-gray-200">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImpactPoint1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImpactPoint2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>{t('roadmapImpactPoint3')}</span>
                </li>
              </ul>
            </section>

            {/* Section 5: SDG Alignment */}
            <section className="bg-purple-800 bg-opacity-30 rounded-lg p-8 backdrop-blur">
              <h2 className="text-2xl font-bold mb-4 text-yellow-400">
                {t('roadmapSDGTitle')}
              </h2>
              <p className="text-gray-200 mb-6">{t('roadmapSDGIntro')}</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('roadmapSDG1Title')}
                  </h3>
                  <p className="text-gray-200 text-sm">
                    {t('roadmapSDG1Text')}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('roadmapSDG2Title')}
                  </h3>
                  <p className="text-gray-200 text-sm">
                    {t('roadmapSDG2Text')}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('roadmapSDG3Title')}
                  </h3>
                  <p className="text-gray-200 text-sm">
                    {t('roadmapSDG3Text')}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('roadmapSDG4Title')}
                  </h3>
                  <p className="text-gray-200 text-sm">
                    {t('roadmapSDG4Text')}
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link to="/interes">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black text-lg px-8 py-4 font-bold">
                {t('registerForInvitation')} →
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
};

export default InnovationRoadmap;
